import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndustry, faCalendarAlt, faPen } from '@fortawesome/free-solid-svg-icons';
import 'flag-icons/css/flag-icons.min.css';
import { useInstallation } from 'modules/Installations/hooks/useInstallations';
import { useAllCountries } from 'modules/Common/utils/Utility';
import { formatDateUTC } from 'helpers/dateParser';

// Bottom-right canvas card with diagram-level metadata: installation name and
// reporting period. The reporting period acts as a button when
// `reportingPeriodInteractive` is true — clicking it opens the period editor
// (handled by the parent via `onClickReportingPeriod`).
//
// Country flag is fetched lazily via useInstallation when installationId
// is provided; React Query dedupes the call against any other installation
// hover/lookup on the page. If the fetch fails or the installation has no
// country, the row degrades to just the name.
export default function DiagramFooterPanel({
  installationId,
  installationName,
  reportingPeriod,
  onClickReportingPeriod,
  reportingPeriodInteractive = false,
}) {
  const { data: installationData } = useInstallation(installationId);
  const countries = useAllCountries();

  if (!reportingPeriod?.from || !reportingPeriod?.to) return null;

  // Resolve the country object regardless of whether the backend returned an
  // embedded country or just an ID — mirrors CgInstallationHoverText.
  const rawCountry = installationData?.address?.country;
  const countryObj =
    rawCountry == null
      ? null
      : typeof rawCountry === 'object'
        ? rawCountry
        : countries.find((c) => `${c._id}` === `${rawCountry}`) || null;
  const iso2 = countryObj?.iso2?.toLowerCase() || null;

  const fromFormatted = formatDateUTC(reportingPeriod.from, 'DD MMM YYYY');
  const toFormatted = formatDateUTC(reportingPeriod.to, 'DD MMM YYYY');

  return (
    <div
      className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden min-w-[200px] max-w-[260px] 2xl:min-w-[260px] 2xl:max-w-[340px]"
      style={{
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
      }}
    >
      {installationName && (
        <div className="flex items-center gap-2 px-2.5 py-1.5 2xl:px-3.5 2xl:py-2 border-b border-gray-200 text-xs 2xl:text-sm text-gray-800">
          <FontAwesomeIcon
            icon={faIndustry}
            className="text-emerald-600 text-xs 2xl:text-sm"
            fixedWidth
          />
          <span className="min-w-0">
            <span className="block text-[10px] 2xl:text-[11px] uppercase tracking-wide text-gray-500 leading-tight">
              Installation
            </span>
            <span
              className="flex items-center gap-1.5 font-medium truncate"
              title={
                countryObj?.name ? `${installationName} (${countryObj.name})` : installationName
              }
            >
              {iso2 && (
                <span
                  className={`fi fi-${iso2} shrink-0 inline-block w-[18px] h-[13px] 2xl:w-[22px] 2xl:h-[16px]`}
                  aria-label={countryObj?.name || ''}
                />
              )}
              <span className="truncate">{installationName}</span>
            </span>
          </span>
        </div>
      )}
      <button
        type="button"
        disabled={!reportingPeriodInteractive}
        onClick={onClickReportingPeriod}
        className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 2xl:px-3.5 2xl:py-2 text-left text-xs 2xl:text-sm text-gray-800 focus:outline-none transition-colors duration-200 ${
          reportingPeriodInteractive ? 'hover:bg-emerald-50 cursor-pointer' : 'cursor-default'
        }`}
        aria-label={
          reportingPeriodInteractive
            ? `Edit reporting period (${fromFormatted} to ${toFormatted})`
            : undefined
        }
      >
        <span className="flex items-center gap-2 min-w-0">
          <FontAwesomeIcon
            icon={faCalendarAlt}
            className="text-emerald-600 text-xs 2xl:text-sm"
            fixedWidth
          />
          <span className="min-w-0">
            <span className="block text-[10px] 2xl:text-[11px] uppercase tracking-wide text-gray-500 leading-tight">
              Reporting Period
            </span>
            <span className="block font-medium truncate">
              {fromFormatted} - {toFormatted}
            </span>
          </span>
        </span>
        {reportingPeriodInteractive && (
          <FontAwesomeIcon
            icon={faPen}
            className="text-gray-400 shrink-0 text-xs 2xl:text-sm"
          />
        )}
      </button>
    </div>
  );
}
